import { __assign, __extends, __rest, __spreadArray } from "tslib";
import * as React from 'react';
import * as TagsInput from 'react-tagsinput';
import AutosizeInput from 'react-input-autosize';
import { namespacedClassnames } from '../../common/namespace-classes';
import { DeprecatedText } from '../../deprecated-typography';
import { KeyCode } from '../../common/key-codes.enum';
import * as classNames from 'classnames';
import { Label, LibColor } from '../../new-libby';
import { Tooltip } from '../tooltip/tooltip';
import { EllipsisText } from '../../composites';
var DEFAULT_MAX_TAGS = -1; // No limit - https://www.youtube.com/watch?v=qM5W7Xn7FiA
var MultiInput = /** @class */ (function (_super) {
    __extends(MultiInput, _super);
    function MultiInput() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.inputRef = null;
        _this.state = {
            validationRejected: false,
            isFocused: false,
        };
        _this.onInputRef = function (ref) { return _this.inputRef = ref; };
        _this.onValidationReject = function () {
            var _a;
            _this.setState({ validationRejected: true });
            if (_this.props.onValidationReject && ((_a = _this.inputRef) === null || _a === void 0 ? void 0 : _a.value)) {
                _this.props.onValidationReject();
            }
        };
        _this.onTagsChange = function (tags) {
            var props = _this.props;
            var maxTags = props.maxTags || DEFAULT_MAX_TAGS;
            _this.setState({ validationRejected: false });
            if (_this.inputRef && props.disableTypingOnMaxTags && tags.length === maxTags) {
                _this.inputRef.blur();
            }
            props.onChange(tags);
        };
        _this.onInputFocus = function () {
            if (_this.inputRef) {
                _this.inputRef.focus();
            }
        };
        _this.autosizingRenderInput = function (props) {
            // https://github.com/olahol/react-tagsinput#how-do-i-fix-warning-unknown-prop-addtag
            var addTag = props.addTag, other = __rest(props, ["addTag"]);
            var isRejected = _this.state.validationRejected ? 'is-rejected' : '';
            var onChange = function (e) {
                props.onChange(e);
                if (!e.target.value && _this.state.validationRejected) {
                    _this.setState({ validationRejected: false });
                }
            };
            var onFocus = function (e) {
                _this.setState({ isFocused: true });
                props.onFocus(e);
            };
            var onBlur = function (e) {
                _this.setState({ isFocused: false });
                props.onBlur(e);
                if (_this.props.onBlur) {
                    _this.props.onBlur();
                }
            };
            return (React.createElement(AutosizeInput, __assign({}, other, { inputClassName: isRejected, inputRef: _this.onInputRef, onFocus: onFocus, onBlur: onBlur, onChange: onChange })));
        };
        _this.maybeTagRenderer = function () {
            if (_this.props.renderTag) {
                return function (props) {
                    var tag = props.tag, key = props.key, disabled = props.disabled, onRemove = props.onRemove, className = props.className, classNameRemove = props.classNameRemove;
                    // filter other unrelated props
                    return _this.props.renderTag && _this.props.renderTag({
                        tag: tag,
                        idx: key,
                        disabled: disabled,
                        onRemove: onRemove,
                        className: className,
                        classNameRemove: classNameRemove
                    });
                };
            }
            else {
                return function (props) {
                    var tag = props.tag, onRemove = props.onRemove, key = props.key, className = props.className, disabled = props.disabled;
                    var onClick = function (_) { return onRemove(key); };
                    var disabledColor = disabled ? LibColor.grey130 : LibColor.sky100;
                    var maybeTagWithEllipsis = _this.props.tagOverflowEllipsis ? (React.createElement(EllipsisText, null,
                        React.createElement("div", null, tag))) : tag;
                    return (React.createElement(Label, { key: key, onRemove: onClick, className: className, color: disabledColor }, maybeTagWithEllipsis));
                };
            }
        };
        return _this;
    }
    MultiInput.prototype.render = function () {
        var props = this.props;
        var maxTags = this.props.maxTags || DEFAULT_MAX_TAGS;
        var maybeHasValues = !!props.values.length ? 'has-values' : '';
        var maybeDisabled = !!props.disabled ? 'disabled' : '';
        var isFocused = this.state.isFocused ? 'focused' : '';
        var isErrored = !!props.errorMessage ? 'with-error' : '';
        var isReachedMaxTags = (props.disableTypingOnMaxTags && this.props.values.length === maxTags) ? true : false;
        var inputPlaceholderProps = !props.placeholder || !!maybeHasValues ?
            { placeholder: '' } : { placeholder: props.placeholder };
        var inputDisabledProps = isReachedMaxTags ? { disabled: true } : { disabled: false };
        var pasteSplitRegex = /[\r\n;]/g;
        var pasteSplit = function (data) { return [
            data
                .replace(pasteSplitRegex, ' ')
                .split(' ')
                .map(function (d) { return d.trim(); })
                .join(' ')
        ]; };
        var maybeIsInvalidMsg = !!props.errorMessage ?
            React.createElement(DeprecatedText, { className: 'invalid-msg', type: 't4a' }, props.errorMessage) : null;
        var baseAddKeys = [KeyCode.TAB, KeyCode.ENTER];
        var addKeys = !!props.additionalAddKeys ? __spreadArray(__spreadArray([], baseAddKeys, true), props.additionalAddKeys, true) : baseAddKeys;
        var namespaceclassNames = namespacedClassnames('multi-input', maybeHasValues, maybeDisabled, props.className, {
            'with-ellipsis': !!props.tagOverflowEllipsis
        });
        var wrapperClassNames = classNames('multi-input-wrapper', isFocused, isErrored, { 'max-tags-disabled': isReachedMaxTags });
        var tagsInput = (React.createElement("div", { className: wrapperClassNames, onClick: this.onInputFocus },
            React.createElement(TagsInput, { renderInput: this.autosizingRenderInput, renderTag: this.maybeTagRenderer(), onChange: this.onTagsChange, value: props.values, validationRegex: props.validationRegex || /.*/i, onValidationReject: this.onValidationReject, addKeys: addKeys, addOnPaste: !props.disableAddOnPaste, onlyUnique: true, addOnBlur: true, maxTags: props.maxTags || DEFAULT_MAX_TAGS, pasteSplit: pasteSplit, inputProps: __assign(__assign({}, inputDisabledProps), inputPlaceholderProps), disabled: !!props.disabled })));
        var disabledTagsInput = (React.createElement(Tooltip, { className: 'disabled-tooltip', body: props.disableTypingOnMaxTags || '' }, tagsInput));
        return (React.createElement("div", { className: namespaceclassNames },
            isReachedMaxTags ? disabledTagsInput : tagsInput,
            maybeIsInvalidMsg));
    };
    return MultiInput;
}(React.PureComponent));
export { MultiInput };
//# sourceMappingURL=multi-input.js.map